import React from 'react';
import { Router, Route, IndexRoute } from 'react-router';
import App from '../components/App';
import SandBoxPage from '../../../../shared/src/pages/SandBoxPage';
import UnauthorizedPage from '../../../../shared/src/pages/UnauthorizedPage';
import RouteNotFound from '../components/RouteNotFound';
import {
  redirectIfNotAuthorized,
  redirectAuthenticated,
  trackPageView,
  changePageTitle,
} from './routeHooks';
import { syncHistory } from '../store/store';

import MainWithSidebar from '../components/layout/MainWithSidebar';
import Main from '../components/layout/Main';
import BookPage from '../../book/BookPage';
import SearchPage from '../../search/SearchPage';
import DocumentPage from '../../document/DocumentPage';
import { resetSearch } from '../../search/searchDucks';

const getDocumentDetails = (store, documentId, attachmentId) => {
  if (!documentId) {
    return {
      title: 'Feuille de route',
    };
  }
  const state = store.getState();
  const document = state.documents.data.find(doc => doc.id === documentId);
  if (document) {
    const attachment = attachmentId && (document.attachments || []).find(att => att.id === attachmentId);
    if (attachment) {
      return {
        title: `Pour aller plus loin - ${attachment.title} - ${document.title}`,
        category: state.documents.activeCategory && state.documents.activeCategory.name,
      };
    }
    return {
      title: `Sujet - ${document.title}`,
      category: state.documents.activeCategory && state.documents.activeCategory.name,
    };
  }
  const archivedDoc = (state.search.historyDocuments || [])
    .find(doc => doc.id === documentId);
  if (archivedDoc) {
    const attachment = attachmentId && (archivedDoc.attachments || []).find(att => att.id === attachmentId);
    if (attachment) {
      return {
        title: `Pour aller plus loin - ${attachment.title} - ${archivedDoc.title}`,
      };
    }
    return {
      title: archivedDoc.title,
    };
  }
  return {
    title: 'Sujet',
  };
};

/**
 * Returns a React-Router hook that:
 *  - sets the page title ;
 *  - tracks the page view ;
 *  - redirects the user if it is not authorized
 *
 * @param store The Redux store.
 * @param pageTitle The page title.
 * @return {Function} The hook.
 */
const trackAndRedirectIfNotAuthorized = (store, pageTitle) => (
  (nextState, replace, next) => {
    changePageTitle(pageTitle);
    trackPageView(nextState.location);
    redirectIfNotAuthorized(nextState, replace, next, store);
  }
);

/**
 * Returns a React-Router hook that:
 *  - sets the page title according to the document to display ;
 *  - tracks the page view ;
 *  - redirects the user if it is not authorized
 *
 * @param store The Redux store.
 * @return {Function} The hook.
 */
const trackDocAndRedirectIfNotAuthorized = store => (
  (nextState, replace, next) => {
    const { params: { documentId, attachmentId } } = nextState;
    const document = getDocumentDetails(store, documentId, attachmentId);
    changePageTitle(`Book - ${document.title}`);
    trackPageView(nextState.location, document.category);
    redirectIfNotAuthorized(nextState, replace, next, store);
  }
);

/**
 * Returns a React-Router hook that:
 *  - sets the page title ;
 *  - tracks the page view ;
 *  - resets the search form ;
 *  - redirects the user if it is not authorized ;
 *
 * @param store The Redux store.
 * @return {Function} The hook.
 */
const homePageHook = store => (
  (nextState, replace, next) => {
    changePageTitle('Book');
    trackPageView(nextState.location);
    store.dispatch(resetSearch());
    redirectIfNotAuthorized(nextState, replace, next, store);
  }
);

export default ({ store }) => (
  <Router history={syncHistory}>
    <Route
      component={DocumentPage}
      path="/documents/:documentId"
      onEnter={trackDocAndRedirectIfNotAuthorized(store)}
    />
    <Route
      component={DocumentPage}
      path="/documents/:documentId/:attachmentId"
      onEnter={trackDocAndRedirectIfNotAuthorized(store)}
    />
    <Route
      component={DocumentPage}
      path="/roadmap"
      onEnter={trackDocAndRedirectIfNotAuthorized(store)}
    />
    <Route component={App}>
      <Route
        component={SandBoxPage}
        path="/sandbox"
        title="Sandbox"
        onEnter={() => {
          redirectAuthenticated(store);
        }}
      />
      <Route
        component={UnauthorizedPage}
        path="/not-authorized"
        onEnter={trackAndRedirectIfNotAuthorized(store, 'Accès non autorisé')}
      />
    </Route>

    <Route component={Main}>
      <Route component={MainWithSidebar} path="/">
        <IndexRoute
          component={BookPage}
          onEnter={homePageHook(store)}
        />
        <Route
          component={SearchPage}
          path="search"
          onEnter={trackAndRedirectIfNotAuthorized(store, 'Book - Recherche')}
        />
      </Route>
    </Route>


    <Route path="*" component={RouteNotFound}/>
  </Router>
);



// WEBPACK FOOTER //
// ./book/src/commons/routes/routes.jsx